import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Pencil, Archive, Search } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Workflow } from '@/types';

export function WorkflowList() {
  const [workflows, setWorkflows] = useState<Workflow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>('all');

  async function load() {
    setError(null);
    try {
      const items = await api.listWorkflows();
      setWorkflows(items);
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      setError(msg);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!workflows) return [];
    return workflows.filter((w) => {
      if (statusFilter !== 'all' && w.status !== statusFilter) return false;
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        w.title.toLowerCase().includes(q) ||
        w.workflow_id.toLowerCase().includes(q) ||
        w.permission.toLowerCase().includes(q) ||
        w.keywords.some((k) => k.toLowerCase().includes(q))
      );
    });
  }, [workflows, filter, statusFilter]);

  async function handleArchive(id: number) {
    if (!window.confirm('Archive this workflow? It will no longer appear in published versions.')) return;
    try {
      await api.deleteWorkflow(id);
      await load();
    } catch (e) {
      const msg = e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message;
      alert(msg);
    }
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Workflows</h2>
          <p className="text-gray-500 mt-1">
            {workflows
              ? `${workflows.length} total · ${workflows.filter((w) => w.status === 'published').length} published`
              : 'Loading…'}
          </p>
        </div>
        <Link to="/workflows/new" className="btn-primary">
          <Plus size={16} className="mr-2" />
          New workflow
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex gap-3 items-center">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="input pl-9"
              placeholder="Search title, id, permission, keywords…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <select
            className="input w-44"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {error && <div className="p-6 text-sm text-red-700 bg-red-50">{error}</div>}

        {workflows && workflows.length === 0 && !error && (
          <div className="p-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">No workflows yet</h3>
            <p className="text-sm text-gray-500 mb-6">
              Create your first workflow to teach the agent how to help users.
            </p>
            <Link to="/workflows/new" className="btn-primary">
              <Plus size={16} className="mr-2" />
              Create the first workflow
            </Link>
          </div>
        )}

        {workflows && workflows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Title
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Permission
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  Updated
                </th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4">
                    <Link to={`/workflows/${w.id}`} className="block">
                      <div className="font-medium text-gray-900 hover:text-brand-700">{w.title}</div>
                      <div className="text-xs font-mono text-gray-500">{w.workflow_id}</div>
                    </Link>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-gray-700">{w.permission}</td>
                  <td className="py-3 px-4">
                    <StatusBadge status={w.status} />
                  </td>
                  <td className="py-3 px-4 text-gray-500 text-xs">
                    {w.updated_at ? new Date(w.updated_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 justify-end">
                      <Link
                        to={`/workflows/${w.id}`}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded transition-colors"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </Link>
                      {w.status !== 'archived' && w.id != null && (
                        <button
                          onClick={() => handleArchive(w.id!)}
                          className="p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded transition-colors"
                          title="Archive"
                        >
                          <Archive size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {workflows && workflows.length > 0 && filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-500">
            No workflows match the current filters.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles: Record<typeof status, string> = {
    draft: 'bg-amber-50 text-amber-700 border-amber-200',
    published: 'bg-green-50 text-green-700 border-green-200',
    archived: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}
